import React, { useState, useEffect } from "react";
import groupBy from "lodash/groupBy";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Button, Space, Row, Spinner } from "@masterteam/mt-design-react";

import Table from "../NewTable";
import Collapse from "../AddNewInitiative/Projects/Collapse";
import ProjectForm from "../AddNewInitiative/Projects/ProjectForm";

import api from "../../providers/api";
import useIsAr from "../../Hooks/useIsAr";
import { addToast, formatDate } from "../../helpers";

import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";

import "./style.scss";

const initialModalState = {
  isOpen: false,
  data: null
};

const InitiativeProjects = ({ data }) => {
  const intl = useIntl();
  const [isAr] = useIsAr();

  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listsLoading, setListLoading] = useState(true);
  const [allWeights, setAllWeights] = useState(0);
  const [formModal, setFormModal] = useState(initialModalState);
  const [lists, setLists] = useState({
    users: [],
    epmoUsers: []
  });

  const [currentUser] = useSelector(({ user }) => [user.user]);

  const isReadOnly = currentUser?.UserName !== data?.ManagerModel?.UserName;

  const projectsByProgram = groupBy(projects, "Program.ID");

  const getInitiativeProjects = async () => {
    setLoading(true);
    try {
      const res = await api.initiatives.getProjectsById(data.ID);
      setAllWeights(
        res.Data.reduce((acc, e) => {
          return acc + parseInt(e.Weight);
        }, 0)
      );
      setProjects(res.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    } finally {
      setLoading(false);
    }
  };

  const getLists = async () => {
    setListLoading(true);
    try {
      const [
        usersRes,
        epmoUsersRes,
        departmentsRes,
        initiativesRes,
        portfoliosRes,
        programsRes
      ] = await Promise.all([
        api.user.getAll(),
        api.user.getPPlusEPMO(),
        api.orgStructure.getAll(),
        api.pPlus.getInitiatives(),
        api.pPlus.getPortfolios(),
        api.pPlus.getPrograms()
      ]);
      setLists((currentLists) => ({
        ...currentLists,
        users: usersRes.StatusCode === "Success" ? usersRes.Data : [],
        epmoUsers:
          epmoUsersRes.StatusCode === "Success" ? epmoUsersRes.Data : [],
        departments:
          departmentsRes.StatusCode === "Success" ? departmentsRes.Data : [],
        initiatives:
          initiativesRes.StatusCode === "Success" ? initiativesRes.Data : [],
        portfolios:
          portfoliosRes.StatusCode === "Success" ? portfoliosRes.Data : [],
        programs: programsRes.StatusCode === "Success" ? programsRes.Data : []
      }));
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    getInitiativeProjects();
    getLists();
  }, []);

  const columns = [
    {
      key: "Name",
      cell: (item) => (isAr ? item?.ArabicName : item?.EnglishName),
      header: intl.formatMessage({
        id: "project.name"
      }),
      width: "25%"
    },
    {
      key: "Duration",
      cell: (item) => item?.Duration,
      header: intl.formatMessage({
        id: "project.duration"
      }),
      width: "15%"
    },
    {
      key: "StartDate",
      cell: (item) => formatDate(item?.StartDate),
      header: intl.formatMessage({ id: "project.start-date" }),
      width: "15%"
    },
    {
      key: "FinishDate",
      cell: (item) => formatDate(item?.FinishDate),
      header: intl.formatMessage({ id: "project.end-date" }),
      width: "15%"
    },
    {
      key: "Budget",
      cell: (item) => item?.Budget,
      header: intl.formatMessage({
        id: "project.budget"
      }),
      width: "15%"
    },
    {
      key: "Weight",
      cell: (item) => `${item?.Weight}%`,
      header: intl.formatMessage({
        id: "project.weight"
      }),
      width: "15%"
    }
  ];

  const actions = [
    {
      key: "edit",
      variant: "primary",
      Icon: EditIcon,
      disabled: (item) => item?.IsCheckedOut,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.update" }),
      onClick: (item) => {
        setFormModal({ isOpen: true, data: item });
      }
    }
  ];

  return (
    <Space className="initiative-projects" direction="vertical">
      <Row align="center" justify="between">
        <div className="title-wrapper">
          {intl.formatMessage({
            id: "initiative.projects"
          })}
        </div>

        {!isReadOnly && (
          <Button
            shape="round"
            onClick={() => setFormModal({ isOpen: true, data: null })}
          >
            {intl.formatMessage({ id: "common.add" })}
          </Button>
        )}
      </Row>

      {loading || listsLoading ? (
        <Row justify="center">
          <Spinner size="large" />
        </Row>
      ) : (
        <Space direction="vertical" size="large">
          {Object.keys(projectsByProgram).map((key) => (
            <Collapse
              program={projectsByProgram[key]?.[0]?.Program}
              programID={key}
              content={
                <Table
                  data={projectsByProgram[key]}
                  columns={columns}
                  actions={isReadOnly ? [] : actions}
                />
              }
            />
          ))}
        </Space>
      )}

      {formModal.isOpen && (
        <ProjectForm
          data={formModal.data}
          lists={lists}
          projects={projects}
          allWeights={allWeights}
          setProjects={setProjects}
          closeModal={() => setFormModal(initialModalState)}
          initiativeData={data}
          getData={getInitiativeProjects}
        />
      )}
    </Space>
  );
};

export default InitiativeProjects;
